import { ArrowRight, Sparkles } from 'lucide-react';
import { ReactNode } from 'react';

interface PremiumCTAProps {
  href: string;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'gradient';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  icon?: boolean;
  glow?: boolean;
  onClick?: () => void;
}

export default function PremiumCTA({
  href,
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  icon = true,
  glow = true,
  onClick,
}: PremiumCTAProps) {
  const baseClasses =
    'group relative inline-flex items-center justify-center gap-3 font-bold rounded-xl transition-all duration-300 transform overflow-hidden';

  const sizeClasses = {
    sm: 'px-6 py-3 text-sm',
    md: 'px-8 py-4 text-base sm:text-lg',
    lg: 'px-10 py-5 text-lg sm:text-xl',
  }[size];

  const variantClasses = {
    primary: `
      bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500
      hover:from-orange-600 hover:via-orange-700 hover:to-orange-600
      text-white
      ${glow ? 'shadow-lg shadow-orange-500/40 hover:shadow-2xl hover:shadow-orange-600/50' : 'shadow-lg hover:shadow-xl'}
      hover:-translate-y-1 hover:scale-[1.02]
      active:translate-y-0 active:scale-[0.98]
    `,
    secondary: `
      bg-gradient-to-r from-green-600 to-green-700
      hover:from-green-700 hover:to-green-800
      text-white
      ${glow ? 'shadow-lg shadow-green-600/40 hover:shadow-2xl hover:shadow-green-700/50' : 'shadow-lg hover:shadow-xl'}
      hover:-translate-y-1 hover:scale-[1.02]
      active:translate-y-0 active:scale-[0.98]
    `,
    gradient: `
      bg-gradient-to-r from-amber-500 via-orange-500 to-red-500
      hover:from-amber-600 hover:via-orange-600 hover:to-red-600
      text-white
      ${glow ? 'shadow-lg shadow-orange-500/50 hover:shadow-2xl hover:shadow-orange-600/60' : 'shadow-lg hover:shadow-xl'}
      hover:-translate-y-1 hover:scale-[1.02]
      active:translate-y-0 active:scale-[0.98]
      animate-gradient
    `,
  }[variant];

  const widthClass = fullWidth ? 'w-full' : 'w-full sm:w-auto';

  return (
    <a
      href={href}
      className={`${baseClasses} ${sizeClasses} ${variantClasses} ${widthClass}`}
      onClick={onClick}
    >
      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 group-hover:translate-x-full"></span>

      <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></span>

      {variant === 'gradient' && (
        <span className="absolute top-0 right-0 w-8 h-8">
          <Sparkles className="w-4 h-4 text-white/50 animate-pulse" />
        </span>
      )}

      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        {icon && (
          <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
        )}
      </span>

      <span className="absolute inset-0 rounded-xl ring-2 ring-white/20 group-hover:ring-white/40 transition-all duration-300"></span>
    </a>
  );
}
